// Generic searchable select — search input triggers a fixed-position dropdown.
// multiple=true (default): selected items render as removable chips above the input.
// multiple=false: single selection, trigger shows the selected label, list shows a checkmark on it.
// variant="light" uses DaisyUI base classes; variant="dark" uses explicit dark colors.
// The search input is the combobox; keyboard/ARIA comes from useListboxNavigation.

import { useState, useRef, useEffect, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useListboxNavigation } from '@startus/ui';

export interface MultiSelectOption {
  label: string;
  value: string;
  group?: string; // when set on every option, dropdown renders grouped headers (cascade-style)
}

interface Props {
  options: MultiSelectOption[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  variant?: 'light' | 'dark';
  multiple?: boolean;
  // Single-select only: renders a leading "clear" entry (e.g. "Any") that resets the selection
  clearLabel?: string;
}

const STYLES = {
  light: {
    trigger:
      'input input-bordered input-sm w-full bg-base-100 text-base-content placeholder:text-base-content/40',
    dropdown:
      'fixed z-[9999] bg-base-100 border border-base-300 rounded-lg shadow-lg flex flex-col',
    search: 'input input-sm input-bordered w-full',
    item: 'px-3 py-2 text-sm cursor-pointer hover:bg-base-200 text-base-content',
    itemGrouped: 'pl-6 pr-3 py-1.5 text-sm cursor-pointer hover:bg-base-200 text-base-content',
    itemActive: 'bg-primary/10 font-medium',
    groupHeader:
      'px-3 pt-2 pb-1 text-xs font-semibold text-base-content/50 uppercase tracking-wide select-none cursor-default',
    empty: 'px-3 py-2 text-sm text-base-content/40 cursor-default select-none',
    chip: 'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-primary/20 text-primary',
    chipRemove: 'hover:text-error leading-none',
    check: 'inline-block w-4 text-primary',
  },
  dark: {
    trigger:
      'w-full bg-[#1a1a1a] border border-white/10 text-white text-sm rounded px-3 py-1.5 placeholder:text-white/40 focus:outline-none focus:border-white/30',
    dropdown:
      'fixed z-[9999] bg-[#1a1a1a] border border-white/10 rounded-lg shadow-lg flex flex-col',
    search:
      'w-full bg-transparent text-white text-sm placeholder:text-white/40 focus:outline-none px-3 py-2 border-b border-white/10',
    item: 'px-3 py-2 text-sm cursor-pointer hover:bg-white/10 text-white',
    itemGrouped: 'pl-6 pr-3 py-1.5 text-sm cursor-pointer hover:bg-white/10 text-white',
    itemActive: 'bg-[#19A2F8]/20 font-medium',
    groupHeader:
      'px-3 pt-2 pb-1 text-xs font-semibold text-white/50 uppercase tracking-wide select-none cursor-default',
    empty: 'px-3 py-2 text-sm text-white/40 cursor-default select-none',
    chip: 'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-[#19A2F8]/20 text-[#19A2F8]',
    chipRemove: 'hover:text-red-400 leading-none',
    check: 'inline-block w-4 text-[#19A2F8]',
  },
} as const;

// Navigable entries in display order: the optional "clear" row, then options (group order).
type NavItem = { kind: 'clear' } | { kind: 'option'; option: MultiSelectOption };

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder,
  variant = 'light',
  multiple = true,
  clearLabel,
}: Props) {
  const { t } = useTranslation('common');
  const s = STYLES[variant];
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Multi-select hides already-picked items (they show as chips instead); single-select keeps
  // the full list visible so the current pick can be seen (checkmark) and re-browsed.
  const filtered = options.filter(
    (o) =>
      (multiple ? !value.includes(o.value) : true) &&
      o.label.toLowerCase().includes(search.toLowerCase())
  );

  // Cascade rendering only kicks in when every option declares a group (e.g. eligibility tags,
  // opportunity types); plain flat lists (industries, regions) render unchanged.
  const isGrouped = options.length > 0 && options.every((o) => o.group);
  const groupOrder = isGrouped ? Array.from(new Set(options.map((o) => o.group))) : [];
  const ordered = isGrouped
    ? groupOrder.flatMap((group) => filtered.filter((o) => o.group === group))
    : filtered;
  const showClear = !multiple && !!clearLabel;
  const navItems: NavItem[] = [
    ...(showClear ? [{ kind: 'clear' } as const] : []),
    ...ordered.map((option) => ({ kind: 'option' as const, option })),
  ];
  const navIndexOf = (option: MultiSelectOption) => ordered.indexOf(option) + (showClear ? 1 : 0);

  const labelFor = useCallback(
    (v: string) => options.find((o) => o.value === v)?.label ?? v,
    [options]
  );

  // Recompute fixed dropdown position on open
  useEffect(() => {
    if (!open || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownStyle({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!containerRef.current?.contains(t) && !dropdownRef.current?.contains(t)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const add = (v: string) => {
    onChange([...value, v]);
    setSearch('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const remove = (v: string) => {
    onChange(value.filter((x) => x !== v));
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const select = (v: string) => {
    onChange([v]);
    setSearch('');
    setOpen(false);
  };

  const clear = () => {
    onChange([]);
    setSearch('');
    setOpen(false);
  };

  const pick = (o: MultiSelectOption) => (multiple ? add(o.value) : select(o.value));

  const listbox = useListboxNavigation({
    items: navItems,
    isOpen: open,
    onSelect: (item) => (item.kind === 'clear' ? clear() : pick(item.option)),
    onOpen: () => setOpen(true),
    onClose: () => {
      setOpen(false);
      setSearch('');
    },
    selectedIndex: multiple
      ? undefined
      : value.length === 0
        ? showClear
          ? 0
          : undefined
        : navItems.findIndex((i) => i.kind === 'option' && i.option.value === value[0]),
  });

  // Keyboard highlight uses the hover background so it reads like the existing hover state.
  const highlight = (index: number) =>
    listbox.isActive(index) ? (variant === 'dark' ? 'bg-white/10' : 'bg-base-200') : '';

  const renderItem = (o: MultiSelectOption, grouped: boolean) => {
    const active = !multiple && value[0] === o.value;
    const index = navIndexOf(o);
    return (
      <li
        key={o.value}
        {...listbox.getOptionProps(index, active)}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => pick(o)}
        className={`${grouped ? s.itemGrouped : s.item} ${highlight(index)} ${active ? s.itemActive : ''}`}
      >
        {!multiple && (
          <span className={s.check} aria-hidden="true">
            {active ? '✓' : ''}
          </span>
        )}
        {o.label}
      </li>
    );
  };

  // Closed single-select trigger shows the picked label; multi-select always shows the live search text.
  const triggerValue = !multiple && !open && value.length > 0 ? labelFor(value[0]) : search;

  return (
    <div ref={containerRef}>
      {/* Selected chips (multi-select only) */}
      {multiple && value.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {value.map((v) => (
            <span key={v} className={s.chip}>
              {labelFor(v)}
              <button
                type="button"
                onClick={() => remove(v)}
                className={s.chipRemove}
                aria-label={t('dropdown.remove', { label: labelFor(v) })}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search trigger */}
      <input
        ref={inputRef}
        type="text"
        value={triggerValue}
        {...listbox.getComboboxProps()}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
          listbox.resetActive();
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder ?? t('dropdown.searchPlaceholder')}
        className={s.trigger}
      />

      {/* Fixed-position dropdown */}
      {open && (
        <div ref={dropdownRef} style={dropdownStyle} className={s.dropdown}>
          {/* Empty state lives outside the listbox (only options/groups may be its children) */}
          {filtered.length === 0 && <p className={s.empty}>{t('common.noResults')}</p>}
          <ul {...listbox.getListboxProps(placeholder)} className="max-h-48 overflow-y-auto">
            {showClear && (
              <li
                {...listbox.getOptionProps(0, value.length === 0)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={clear}
                className={`${s.item} ${highlight(0)} ${value.length === 0 ? s.itemActive : ''}`}
              >
                <span className={s.check} aria-hidden="true">
                  {value.length === 0 ? '✓' : ''}
                </span>
                {clearLabel}
              </li>
            )}
            {isGrouped
              ? groupOrder.map((group) => {
                  const itemsInGroup = filtered.filter((o) => o.group === group);
                  if (itemsInGroup.length === 0) return null;
                  // role="group" names the group for screen readers; the visual header is hidden
                  // from them so it isn't read twice.
                  return (
                    <li key={group} role="presentation">
                      <ul role="group" aria-label={group}>
                        <li role="presentation" aria-hidden="true" className={s.groupHeader}>
                          {group}
                        </li>
                        {itemsInGroup.map((o) => renderItem(o, true))}
                      </ul>
                    </li>
                  );
                })
              : filtered.map((o) => renderItem(o, false))}
          </ul>
        </div>
      )}
    </div>
  );
}
